"use client";

import { RefObject, useEffect, useLayoutEffect, useRef, useState } from "react";
import { SECONDARY_NAV_ID } from "./constants";

// Hook to determine if a position: a sticky element is stuck or placed normally
export const useIsStuck = (
  ref: RefObject<HTMLElement | null>,
  offsetTop: number = 0,
) => {
  const [isStuck, setIsStuck] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([e]) => {
        setIsStuck(e.intersectionRatio < 1);
      },
      { threshold: 1, rootMargin: `${-offsetTop - 1}px` },
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [ref, offsetTop]);

  return isStuck;
};

// Hook to determine the active section based on IntersectionObserver
export const useActiveSection = (
  sectionIds: string[],
  offsetTop: number = 0,
) => {
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const activeSectionMapRef = useRef<Map<string, boolean>>(new Map());

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          activeSectionMapRef.current.set(
            entry.target.id,
            entry.isIntersecting,
          );
        });
        const active = sectionIds.find(
          (id) => activeSectionMapRef.current.get(id) === true,
        );
        setActiveSection(active || null);
      },
      {
        rootMargin: `-${offsetTop + 1}px 0px 0px 0px`,
        threshold: 0,
      },
    );

    sectionIds.forEach((id) => {
      const sectionElement = document.getElementById(id);
      if (sectionElement) {
        observer.observe(sectionElement);
      }
    });

    return () => {
      observer.disconnect();
    };
  }, [sectionIds, offsetTop]);

  return activeSection;
};

// Custom hook to apply scroll-margin-top to all observed sections
export const useScrollMarginTop = (
  sectionIds: string[],
  offsetTop: number,
  stickyNavigationHeight: number,
) => {
  useEffect(() => {
    const scrollMarginTopValue = offsetTop + stickyNavigationHeight;

    sectionIds.forEach((id) => {
      const sectionElement = document.getElementById(id);
      if (sectionElement) {
        sectionElement.style.scrollMarginTop = `${scrollMarginTopValue}px`;
      }
    });

    return () => {
      sectionIds.forEach((id) => {
        const sectionElement = document.getElementById(id);
        if (sectionElement) {
          sectionElement.style.scrollMarginTop = "";
        }
      });
    };
  }, [sectionIds, offsetTop, stickyNavigationHeight]);
};

// Hook that observes the height of the sticky navigation element.
// Used to calculate scroll-margin-top dynamically when nav content wraps.
export const useNavHeight = (
  ref: RefObject<HTMLElement | null>,
  defaultHeight: number = 52,
) => {
  const [height, setHeight] = useState(defaultHeight);

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;

    const initialHeight = element.getBoundingClientRect().height;
    setHeight(initialHeight);

    const resizeObserver = new ResizeObserver(() => {
      const newHeight = element.getBoundingClientRect().height;
      setHeight(newHeight);
    });

    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
    };
  }, [ref, defaultHeight]);

  return height;
};

// Hook that observes the height of the SecondaryNavigation component.
// Used to calculate the correct offset for StickyNavigation positioning.
export const useSecondaryNavHeight = () => {
  const [height, setHeight] = useState(0);

  useLayoutEffect(() => {
    const secondaryNav = document.getElementById(SECONDARY_NAV_ID);

    if (!secondaryNav) {
      return;
    }

    const initialHeight = secondaryNav.getBoundingClientRect().height;
    setHeight(initialHeight);

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setHeight(entry.contentRect.height);
      }
    });

    resizeObserver.observe(secondaryNav);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return height;
};
